import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {LoadingStates} from '@Utils/loading.util';
import {
  closeAiActionLoadingModal,
  isCurrentModalSession,
  openMakeTextEditableLoadingModalAndGetSessionId,
  showMakeTextEditableReadyModal,
} from '@Components/ai-chat-window/lib/ai-action-loading-modal.library';
import {
  isMakeTextEditableHandedOffToGrowl,
  setMakeTextEditableConversionPending,
  showMakeTextEditableDailyLimitGrowl,
  showMakeTextEditableDoneGrowl,
  showMakeTextEditableErrorGrowl,
} from '@Components/ai-chat-window/lib/make-text-editable-growl.library';
import {
  createPosterFromAiChatAsset,
  ensureMakeTextEditableCredits,
  getMakeTextEditableErrorCode,
  MakeTextEditableErrorCode,
  type MakeTextEditableTextRemovalMethod,
  startMakeTextEditableWithPreCreatedPoster,
} from '@Components/ai-chat-window/lib/make-text-editable.library';
import {AiChatOpenEditorType, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {getUrlWithLayeringFeedbackParam} from '@Components/ai-chat-window/lib/make-text-editable-feedback.library';
import {LayeringType} from '@Libraries/layering-type';

interface UseMakeTextEditableProps {
  chatId: string;
}

interface UseMakeTextEditable {
  makeTextEditable: (hashedId: string, textRemovalMethod?: MakeTextEditableTextRemovalMethod, hasLayeredPoster?: boolean) => Promise<void>;
}

interface OpenLayersEditorTabOptions {
  assetHashedFilename: string;
  jobId: string;
  posterHashedId: string;
  layersReady: boolean;
}

/**
 * AI Chat "Make text editable" action — mirrors `useOpenChatImageInEditor` shape.
 * Pre-checks credits (MAKE_TEXT_EDITABLE_COST) and bails out cleanly if the
 * user declines the top-up dialog; otherwise opens the existing AI-action
 * loading modal, POSTs to /imagetodesign/createFromAiChatAsset, and on success
 * switches the modal into "Ready" mode with an "Open in editor" CTA.
 */
export const useMakeTextEditable = ({chatId}: UseMakeTextEditableProps): UseMakeTextEditable => {
  const {state, actions} = useAiChat();

  const setMteLoadingState = (hashedId: string, loadingState: LoadingStates): void => {
    actions.updateLoadingStatesForImageActions(chatId, hashedId, {makeTextEditableLoadingState: loadingState});
  };

  /**
   * Routes a failed conversion to the right growl: when the backend authoritatively rejects with the
   * daily-limit code (the in-chat gate ran on a stale snapshot) it shows the daily-limit growl and refreshes
   * the limit so the menu reflects reality; any other failure shows the generic conversion-error growl.
   */
  const handleMakeTextEditableError = (e: unknown): void => {
    if (getMakeTextEditableErrorCode(e) === MakeTextEditableErrorCode.DAILY_LIMIT_REACHED) {
      showMakeTextEditableDailyLimitGrowl();
      void actions.refreshMakeTextEditableLimit();
      return;
    }
    showMakeTextEditableErrorGrowl();
  };

  const makeTextEditable = async (hashedId: string, textRemovalMethod?: MakeTextEditableTextRemovalMethod, hasLayeredPoster = false): Promise<void> => {
    if (!hasLayeredPoster && state.chargeForMakeTextEditable) {
      let hasCredits: boolean;
      try {
        hasCredits = await ensureMakeTextEditableCredits();
      } catch (e: unknown) {
        showMakeTextEditableErrorGrowl();
        console.error(e);
        return;
      }
      if (!hasCredits) {
        return;
      }
    }

    if (window.innerWidth < MOBILE_MODAL_WIDTH_THRESHOLD) {
      await runMobileMakeTextEditable(hashedId, textRemovalMethod, hasLayeredPoster);
    } else {
      await runDesktopMakeTextEditable(hashedId, textRemovalMethod);
    }
  };

  /**
   * Mobile: keeps the in-chat flow — loading modal, poll the job here, then hand off to the ready modal or
   * the growl when done. Unchanged from the original single-tab behaviour. Fresh (non-reused) posters carry
   * the `?layeringFeedback=text` flag so the editor shows the post-layering feedback toast on load.
   */
  const runMobileMakeTextEditable = async (hashedId: string, textRemovalMethod?: MakeTextEditableTextRemovalMethod, hasLayeredPoster = false): Promise<void> => {
    setMteLoadingState(hashedId, LoadingStates.LOADING);
    setMakeTextEditableConversionPending(true);
    const sessionId = openMakeTextEditableLoadingModalAndGetSessionId();

    try {
      const outcome = await createPosterFromAiChatAsset(hashedId, textRemovalMethod);

      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_OPEN_EDITOR, {[GA4EventParamName.TYPE]: AiChatOpenEditorType.EDITABLE_LAYERS});

      setMteLoadingState(hashedId, LoadingStates.LOADED);

      openDebugViewIfPresent(outcome.debugViewUrl);

      setMakeTextEditableConversionPending(false);
      const navigationUrl = hasLayeredPoster ? outcome.posterUrl : getUrlWithLayeringFeedbackParam(outcome.posterUrl, LayeringType.TEXT);
      if (isMakeTextEditableHandedOffToGrowl()) {
        showMakeTextEditableDoneGrowl(navigationUrl);
      } else if (sessionId !== undefined && isCurrentModalSession(sessionId)) {
        showMakeTextEditableReadyModal(navigationUrl);
      }
    } catch (e: unknown) {
      setMteLoadingState(hashedId, LoadingStates.NOT_LOADED);
      setMakeTextEditableConversionPending(false);
      if (!isMakeTextEditableHandedOffToGrowl()) {
        closeAiActionLoadingModal();
      }
      handleMakeTextEditableError(e);
      console.error(e);
    }
  };

  /**
   * Desktop: opens the editor in a new tab immediately on a pre-created poster, then the editor tab itself
   * polls the job and reveals the layers in place. No in-chat modal/poll — credits are already confirmed.
   */
  const runDesktopMakeTextEditable = async (hashedId: string, textRemovalMethod?: MakeTextEditableTextRemovalMethod): Promise<void> => {
    setMteLoadingState(hashedId, LoadingStates.LOADING);

    try {
      const {jobId, posterHashedId, layersReady, debugViewUrl} = await startMakeTextEditableWithPreCreatedPoster(hashedId, textRemovalMethod);
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_OPEN_EDITOR, {[GA4EventParamName.TYPE]: AiChatOpenEditorType.EDITABLE_LAYERS});
      setMteLoadingState(hashedId, LoadingStates.LOADED);
      openLayersEditorTab({assetHashedFilename: hashedId, jobId, posterHashedId, layersReady});
      openDebugViewIfPresent(debugViewUrl);
    } catch (e: unknown) {
      setMteLoadingState(hashedId, LoadingStates.NOT_LOADED);
      handleMakeTextEditableError(e);
      console.error(e);
    }
  };

  return {makeTextEditable};
};

/**
 * Opens the desktop "Editable layers" editor tab. When the layered poster already exists (reuse) it opens
 * the poster directly; otherwise it opens the layers-pending page that shows the original image and polls.
 * Opened synchronously after the start call resolves so Chrome's transient activation usually survives; if
 * the browser still blocks the pop-up, a growl offers a click-to-open action (no re-run, no extra credits).
 */
function openLayersEditorTab({assetHashedFilename, jobId, posterHashedId, layersReady}: OpenLayersEditorTabOptions): void {
  const url = layersReady
    ? window.PMW.util.site_url(`posterbuilder/load/${posterHashedId}`)
    : window.PMW.util.site_url(
        `posterbuilder/loadFromAiChatLayersPending/${posterHashedId}?jobId=${encodeURIComponent(jobId)}&assetHashedFilename=${encodeURIComponent(assetHashedFilename)}`
      );

  const tab = window.open(url, '_blank');
  if (tab) {
    return;
  }

  showMessageGrowl({
    key: 'mte-popup-blocked',
    text: window.i18next.t('pmwjs_mte_popup_blocked_open_design'),
    type: GROWL_TYPE.NEUTRAL,
    showCloseIcon: true,
    action: {label: window.i18next.t('pmwjs_open_design'), url, openInNewTab: true},
  });
}

/**
 * Opens the debug overlay tab at the URL the backend chose to expose. The super-user + env-flag
 * decision is made entirely server-side: the URL is present only when the overlay should open and
 * null otherwise, so this is a silent no-op in every other case. Browsers can block the popup
 * because this fires after the async polling resolves (the original user-click context is stale);
 * when blocked the user can still navigate manually via the success modal's CTA.
 */
function openDebugViewIfPresent(debugViewUrl: string | null): void {
  if (!debugViewUrl) {
    return;
  }
  window.open(debugViewUrl, '_blank', 'noopener,noreferrer');
}
